import { Button } from '@src/components/ui/button';

import { css } from '@emotion/css';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import '@src/components/ui/table.css';

type Props = {
    total: number;
    page: number;
    pageSize: number;
    onPageChange: (page: number) => void;
};

export function TableFieldPagination({
    total,
    page: currentPage,
    pageSize,
    onPageChange,
}: Props) {
    const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);

    if (totalPages <= 1) return null;

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            onPageChange(newPage);
        }
    };

    const pageNumbers: (number | string)[] = [];
    if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i++) {
            pageNumbers.push(i);
        }
    } else {
        pageNumbers.push(1);
        if (currentPage > 3) {
            pageNumbers.push('...');
        }
        for (
            let i = Math.max(2, currentPage - 1);
            i <= Math.min(totalPages - 1, currentPage + 1);
            i++
        ) {
            pageNumbers.push(i);
        }
        if (currentPage < totalPages - 2) {
            pageNumbers.push('...');
        }
        pageNumbers.push(totalPages);
    }

    return (
        <div className='flex w-full items-center justify-start md:justify-end'>
            <div className='flex items-center gap-3'>
                <Button
                    disabled={currentPage <= 1}
                    size='sm'
                    type='button'
                    variant='ghost'
                    paddingX={11}
                    paddingY={7.5}
                    className={css`
                        min-height: 36px;
                        padding: 7.5px 11px;
                        opacity: ${currentPage <= 1 ? 0.5 : 1};
                    `}
                    onClick={() => handlePageChange(currentPage - 1)}
                >
                    <ChevronLeft className='size-3.5' />
                </Button>

                {pageNumbers.map((page, index) =>
                    page === '...' ? (
                        <span
                            className='table-field-pagination__ellipsis'
                            key={`ellipsis-${index}`}
                        >
                            <MoreHorizontal className='size-4' />
                        </span>
                    ) : (
                        <Button
                            key={page}
                            size='sm'
                            type='button'
                            variant='ghost'
                            height={36}
                            paddingX={8}
                            paddingY={8}
                            fontWeight={600}
                            className={`min-w-[34px] ${
                                page === currentPage
                                    ? 'bg-(--color-primary-1)! text-(--color-brand-green)!'
                                    : 'bg-(--color-neutral-3)! text-(--color-card-text)!'
                            }`}
                            onClick={() => handlePageChange(page as number)}
                        >
                            {page}
                        </Button>
                    ),
                )}

                <Button
                    disabled={currentPage >= totalPages}
                    size='sm'
                    type='button'
                    variant='ghost'
                    paddingX={11}
                    paddingY={7.5}
                    className={css`
                        min-height: 36px;
                        color: var(--color-card-text);
                        padding: 7.5px 11px;
                        background-color: var(--color-neutral-3) !important;
                    `}
                    onClick={() => handlePageChange(currentPage + 1)}
                >
                    <ChevronRight className='size-3.5' />
                </Button>
            </div>
        </div>
    );
}
